import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PhoenixLogo } from './PhoenixLogo';
import { useAuth } from '../hooks/useAuth';
import { C } from '../utils/constants';
import { ChangePasswordModal } from './ChangePasswordModal';

export const Nav = ({ user, onAdmin }) => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [showMenu, setShowMenu] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };
  // Avatar initials, derived from the user's name (fields are camelCase from
  // GET /api/user/me: firstName, lastName). Fallback chain:
  //   first + last → two initials (e.g. "Ben Milston" → "BM")
  //   first only   → one initial  (e.g. "Ben" → "B")
  //   no name      → first letter of email, uppercased (never blank)
  const fn = (user?.firstName || '').trim();
  const ln = (user?.lastName || '').trim();
  const initials = (fn && ln)
    ? (fn.charAt(0) + ln.charAt(0)).toUpperCase()
    : fn
      ? fn.charAt(0).toUpperCase()
      : (user?.email || 'You').charAt(0).toUpperCase();

  return (
    <nav style={{
      background: `linear-gradient(135deg, ${C.darkBrown} 0%, ${C.brown} 100%)`,
      padding: '0 24px',
      display: 'flex',
      alignItems: 'center',
      gap: 16,
      height: 68,
      position: 'sticky',
      top: 0,
      zIndex: 100,
      boxShadow: '0 2px 16px rgba(61,31,2,0.25)',
    }}>
      {/* Logo — navigates to dashboard */}
      <button
        onClick={() => navigate('/dashboard')}
        style={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          padding: 0,
          display: 'flex',
          alignItems: 'center',
          flexShrink: 0,
        }}
        aria-label="Go to dashboard"
      >
        <PhoenixLogo size={52} nav />
      </button>

      {/* Brand name — hidden on mobile */}
      <span
        className="nav-title"
        style={{
          fontFamily: "'Forum', serif",
          color: '#fff',
          fontSize: '1.25rem',
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          whiteSpace: 'nowrap',
        }}
      >
        Recondition Yourself
      </span>

      {/* Spacer */}
      <div style={{ flex: 1 }} />

      {/* My Journal button — hidden on mobile */}
      <button
        onClick={() => navigate('/my-journal')}
        className="nav-community"
        style={{
          fontFamily: "'Forum', serif",
          color: C.brown,
          background: C.gold,
          border: `1.5px solid ${C.gold}`,
          borderRadius: 7,
          padding: '7px 18px',
          fontSize: '1rem',
          letterSpacing: '0.04em',
          whiteSpace: 'nowrap',
          transition: 'background 0.2s',
          cursor: 'pointer',
          textDecoration: 'none',
          display: 'inline-flex',
          alignItems: 'center',
          lineHeight: '1.5',
          boxSizing: 'border-box',
        }}
        onMouseEnter={e => e.currentTarget.style.background = C.amber}
        onMouseLeave={e => e.currentTarget.style.background = C.gold}
      >
        My Journal
      </button>

      {/* Community button — hidden on mobile */}
      <a
        href="https://www.facebook.com/groups/reconditionyourself"
        target="_blank"
        rel="noopener noreferrer"
        className="nav-community"
        style={{
          fontFamily: "'Forum', serif",
          color: '#fff',
          background: '#1877F2',
          border: '1.5px solid #1877F2',
          borderRadius: 7,
          padding: '7px 18px',
          fontSize: '1rem',
          letterSpacing: '0.04em',
          whiteSpace: 'nowrap',
          transition: 'background 0.2s',
          cursor: 'pointer',
          textDecoration: 'none',
          display: 'inline-flex',
          alignItems: 'center',
          lineHeight: '1.5',
          boxSizing: 'border-box',
        }}
        onMouseEnter={e => e.currentTarget.style.background = '#0f6ae0'}
        onMouseLeave={e => e.currentTarget.style.background = '#1877F2'}
      >
        Community
      </a>

      {/* Admin button — hidden on mobile, only shown for admins */}
      {user?.isAdmin && (
        <button
          onClick={onAdmin}
          className="nav-admin"
          style={{
            fontFamily: "'Forum', serif",
            color: C.terracotta,
            border: `1.5px solid ${C.terracotta}`,
            borderRadius: 7,
            padding: '7px 18px',
            fontSize: '0.9rem',
            letterSpacing: '0.04em',
            whiteSpace: 'nowrap',
            background: 'transparent',
            cursor: 'pointer',
            transition: 'background 0.2s',
          }}
          onMouseEnter={e => e.currentTarget.style.background = 'rgba(173,121,91,0.12)'}
          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
        >
          Admin
        </button>
      )}

      {showChangePassword && (
        <ChangePasswordModal onClose={() => setShowChangePassword(false)} />
      )}

      {/* User avatar + logout dropdown */}
      <div style={{ position: 'relative', flexShrink: 0 }}>
        <div
          onClick={() => setShowMenu(m => !m)}
          style={{
            width: 38,
            height: 38,
            borderRadius: '50%',
            background: `linear-gradient(135deg, ${C.gold}, ${C.amber})`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontFamily: "'Forum', serif",
            fontSize: '1.1rem',
            color: C.darkBrown,
            fontWeight: 700,
            boxShadow: '0 2px 8px rgba(245,166,35,0.4)',
            cursor: 'pointer',
            userSelect: 'none',
          }}
        >
          {initials}
        </div>

        {showMenu && (
          <>
            {/* Backdrop */}
            <div
              onClick={() => setShowMenu(false)}
              style={{ position: 'fixed', inset: 0, zIndex: 99 }}
            />
            {/* Dropdown */}
            <div style={{
              position: 'absolute',
              top: 46,
              right: 0,
              background: '#fff',
              borderRadius: 10,
              boxShadow: '0 8px 32px rgba(61,31,2,0.18)',
              border: `1px solid ${C.border}`,
              minWidth: 160,
              zIndex: 200,
              overflow: 'hidden',
            }}>
              <button
                onClick={() => { setShowMenu(false); navigate('/dashboard'); }}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  background: 'none',
                  border: 'none',
                  borderBottom: `1px solid ${C.border}`,
                  textAlign: 'left',
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                  color: C.brown,
                  fontFamily: "'Nunito', sans-serif",
                  transition: 'background 0.15s',
                }}
                onMouseEnter={e => e.currentTarget.style.background = C.veryLightBrown}
                onMouseLeave={e => e.currentTarget.style.background = 'none'}
              >
                Home
              </button>
              <button
                onClick={() => { setShowMenu(false); navigate('/my-journal'); }}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  background: 'none',
                  border: 'none',
                  borderBottom: `1px solid ${C.border}`,
                  textAlign: 'left',
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                  color: C.brown,
                  fontFamily: "'Nunito', sans-serif",
                  transition: 'background 0.15s',
                }}
                onMouseEnter={e => e.currentTarget.style.background = C.veryLightBrown}
                onMouseLeave={e => e.currentTarget.style.background = 'none'}
              >
                My Journal
              </button>
              {user?.isAdmin && (
                <button
                  onClick={() => { setShowMenu(false); onAdmin(); }}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    background: 'none',
                    border: 'none',
                    borderBottom: `1px solid ${C.border}`,
                    textAlign: 'left',
                    cursor: 'pointer',
                    fontSize: '0.9rem',
                    color: C.terracotta,
                    fontFamily: "'Nunito', sans-serif",
                    transition: 'background 0.15s',
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = C.veryLightBrown}
                  onMouseLeave={e => e.currentTarget.style.background = 'none'}
                >
                  Admin
                </button>
              )}
              <button
                onClick={() => { setShowMenu(false); setShowChangePassword(true); }}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  background: 'none',
                  border: 'none',
                  borderBottom: `1px solid ${C.border}`,
                  textAlign: 'left',
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                  color: C.brown,
                  fontFamily: "'Nunito', sans-serif",
                  transition: 'background 0.15s',
                }}
                onMouseEnter={e => e.currentTarget.style.background = C.veryLightBrown}
                onMouseLeave={e => e.currentTarget.style.background = 'none'}
              >
                Change Password
              </button>
              <button
                onClick={handleLogout}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  background: 'none',
                  border: 'none',
                  textAlign: 'left',
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                  color: C.brown,
                  fontFamily: "'Nunito', sans-serif",
                  transition: 'background 0.15s',
                }}
                onMouseEnter={e => e.currentTarget.style.background = C.veryLightBrown}
                onMouseLeave={e => e.currentTarget.style.background = 'none'}
              >
                Sign Out
              </button>
            </div>
          </>
        )}
      </div>
    </nav>
  );
};
